import React, { useCallback, useEffect, useState } from "react";

import SideMenu from "../../../components/SideMenu";
import Footer from "../../../components/Footer";
import Quotes from "../../../components/Quotes";
import { getTranslations } from "../../../services/languageUtils";
import MedService from "../../../services/Gowd/medService";

/**
 * MED — Mecanismo Especial de Devolução.
 *
 * ── Por que esta tela existe ──
 *
 * É o processo do Banco Central para pedir de volta um PIX recebido de forma indevida. Quem
 * pagou abre o caso; quem recebeu tem PRAZO para analisar e devolver.
 *
 * Sem ela, um caso aberto CONTRA a conta só aparecia quando alguém olhava o painel do
 * provedor — e o prazo corre sozinho. É o único recurso desta entrega em que a ausência custa
 * dinheiro por inação, não por erro.
 *
 * ── O layout é o das outras telas ──
 *
 * As classes (`pixTransferComponent`, `mainContent100`, `diplay-inline`, `input`, `buttonSend`)
 * só existem DENTRO de `.pixTransferComponent`, em PixTransfer.scss. A primeira versão desta
 * tela inventou as suas e ficou sem formatação nenhuma.
 *
 * ── O que a tela prioriza ──
 *
 * `awaitingOurAnalysis` primeiro: é o campo que diz "o prazo está correndo contra você", e
 * ordenar por data deixaria isso no meio de casos já resolvidos.
 */

const TIPOS = [
  { value: "RECOVERY_VALUE", label: "Devolução de valor" },
  { value: "INFRINGEMENT_NOTIFICATION", label: "Notificação de infração" },
  { value: "REFUND_REQUEST", label: "Pedido de reembolso" },
];

const PAPEIS = [
  { value: "", label: "Todos" },
  { value: "RECEIVER", label: "Contra mim (recebi)" },
  { value: "PAYER", label: "Abertos por mim (paguei)" },
];

function rotuloTipo(valor) {
  return TIPOS.find((t) => t.value === valor)?.label || valor || "—";
}

export default function Med() {
  const [translations, setTranslations] = useState({});
  const [aba, setAba] = useState("casos");

  const [casos, setCasos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [aviso, setAviso] = useState("");

  const [somentePendentes, setSomentePendentes] = useState(true);
  const [papel, setPapel] = useState("");

  const [abrindo, setAbrindo] = useState(false);
  const [form, setForm] = useState({
    endToEndId: "",
    objectType: "RECOVERY_VALUE",
    reason: "",
    details: "",
    amount: "",
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      const res = await MedService.list({
        onlyPending: somentePendentes,
        role: papel || undefined,
        limit: 100,
      });

      const data = res?.data?.data ?? res?.data;
      const lista = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

      // Quem espera análise NOSSA vem primeiro: é onde o prazo corre.
      setCasos(
        [...lista].sort(
          (a, b) =>
            Number(Boolean(b.awaitingOurAnalysis)) - Number(Boolean(a.awaitingOurAnalysis)),
        ),
      );
    } catch (err) {
      console.error("Erro ao listar casos de MED:", err);
      setErro(
        err?.response?.data?.message ||
          "Não foi possível carregar os casos de MED desta conta.",
      );
      setCasos([]);
    } finally {
      setCarregando(false);
    }
  }, [somentePendentes, papel]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    getTranslations(localStorage.getItem("language") || "pt").then(setTranslations);
  }, []);

  const set = (campo) => (event) => {
    setForm({ ...form, [campo]: event.target.value });
    setErro("");
  };

  const podeAbrir = form.endToEndId.trim() && form.reason.trim();

  async function abrir() {
    setErro("");
    setAviso("");
    setAbrindo(true);

    try {
      await MedService.open({
        endToEndId: form.endToEndId.trim(),
        objectType: form.objectType,
        reason: form.reason.trim(),
        details: form.details.trim() || undefined,
        amount: form.amount.trim() || undefined,
      });

      setAviso("Caso aberto. Acompanhe na aba de casos.");
      setForm({ endToEndId: "", objectType: "RECOVERY_VALUE", reason: "", details: "", amount: "" });
      setAba("casos");
      await carregar();
    } catch (err) {
      // A frase vem do servidor e nomeia o campo (transação inexistente, tipo inválido, valor
      // com vírgula). Um texto genérico faria repetir o mesmo erro com o prazo correndo.
      setErro(
        err?.response?.data?.message ||
          "Não foi possível abrir o caso. Confira os dados e tente de novo.",
      );
    } finally {
      setAbrindo(false);
    }
  }

  async function cancelar(caso) {
    const id = caso.referenceId || caso.recoveryId;
    if (!id) {
      setErro("Este caso não tem identificador para cancelamento.");
      return;
    }

    setErro("");
    try {
      await MedService.cancel(id, "Cancelado pelo cliente");
      setAviso("Caso cancelado.");
      await carregar();
    } catch (err) {
      setErro(err?.response?.data?.message || "Não foi possível cancelar o caso.");
    }
  }

  return (
    <section id="userPage" className="userPage transactionPage">
      <SideMenu />

      <div className="contentData">
        <div className="contentScroll">
          <Quotes />

          <p className="breadcrumb">
            <b>
              <span className="textPrimary">
                {translations.home} | {translations.account || "Conta"}
              </span>{" "}
              | MED
            </b>
          </p>
          <br />
          <p className="title">
            <span style={{ fontWeight: "normal" }}>{translations.account || "Conta"} |</span> MED
          </p>
          <br />

          <div className="contentSubData">
            {/* As abas seguem o padrão das telas de PIX e TED. */}
            <div
              className={"contentButtonPix " + (aba === "casos" ? "contentButtonPixSelected" : "")}
              onClick={() => setAba("casos")}
            >
              Casos
            </div>
            <div
              className={"contentButtonPix " + (aba === "abrir" ? "contentButtonPixSelected" : "")}
              onClick={() => setAba("abrir")}
            >
              Abrir caso
            </div>

            <section id="pixTransferComponent" className="pixTransferComponent">
              {aba === "casos" ? (
                <div className="mainContent100">
                  <p className="pTitle">Casos de MED</p>
                  <p className="pLabel">
                    Pedidos de devolução de PIX. Casos <b>contra você</b> têm prazo de resposta.
                  </p>

                  <div className="diplay-inline">
                    <p>Papel</p>
                    <select
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={papel}
                      onChange={(e) => setPapel(e.target.value)}
                    >
                      {PAPEIS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="diplay-inline">
                    <p>Situação</p>
                    <select
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={somentePendentes ? "abertos" : "todos"}
                      onChange={(e) => setSomentePendentes(e.target.value === "abertos")}
                    >
                      {/* Caso aberto tem prazo e é o único que exige ação — é o padrão. */}
                      <option value="abertos">Somente em aberto</option>
                      <option value="todos">Todos</option>
                    </select>
                  </div>

                  {erro ? (
                    <p style={{ color: "#b42318", fontWeight: "bold", fontSize: 13 }}>{erro}</p>
                  ) : null}
                  {aviso ? <p className="pLabel">{aviso}</p> : null}

                  {carregando ? (
                    <p className="labelGray">Carregando…</p>
                  ) : casos.length === 0 ? (
                    <p className="labelGray">
                      {somentePendentes ? "Nenhum caso em aberto." : "Nenhum caso nesta conta."}
                    </p>
                  ) : (
                    casos.map((caso) => (
                      <div
                        key={caso.referenceId || caso.recoveryId || caso.endToEndId}
                        style={{
                          border: caso.awaitingOurAnalysis ? "1px solid #f59e0b" : "1px solid #eaecf0",
                          background: caso.awaitingOurAnalysis ? "#fffbeb" : "#ffffff",
                          borderRadius: 8,
                          padding: 12,
                          marginTop: 12,
                        }}
                      >
                        <div className="contentSpaceBetween">
                          <p>
                            <b>{rotuloTipo(caso.objectType)}</b>
                          </p>
                          <p className="labelGray">
                            {caso.role === "PAYER" ? "aberto por mim" : "contra mim"} ·{" "}
                            {caso.status || "—"}
                          </p>
                        </div>

                        {/*
                          O aviso de prazo tem destaque e texto próprio: "aguardando análise" no
                          meio dos outros campos não diz que há algo a fazer hoje.
                        */}
                        {caso.awaitingOurAnalysis ? (
                          <p style={{ color: "#b45309", fontWeight: "bold", fontSize: 13 }}>
                            Aguardando a sua análise — o prazo está correndo.
                          </p>
                        ) : null}

                        <div className="contentSpaceBetween">
                          <p className="labelGray">Transação</p>
                          <p style={{ fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere" }}>
                            {caso.endToEndId || "—"}
                          </p>
                        </div>
                        <div className="contentSpaceBetween">
                          <p className="labelGray">Valor</p>
                          <p>
                            <b>{caso.amount || "—"}</b>
                            {caso.returnedAmount ? ` · devolvido: ${caso.returnedAmount}` : ""}
                          </p>
                        </div>
                        {caso.reason ? (
                          <div className="contentSpaceBetween">
                            <p className="labelGray">Motivo</p>
                            <p>{caso.reason}</p>
                          </div>
                        ) : null}

                        {/*
                          Cancelar só no que foi aberto POR NÓS: caso contra a conta não se
                          cancela, responde-se — e o botão ali prometeria uma ação que o
                          provedor recusa.
                        */}
                        {caso.role === "PAYER" ? (
                          <div
                            className="backButton"
                            style={{ marginTop: 10 }}
                            onClick={() => cancelar(caso)}
                          >
                            Cancelar caso
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="mainContent100">
                  <p className="pTitle">Abrir um caso</p>
                  <p className="pLabel">
                    Para contestar um PIX que você pagou de forma indevida.
                  </p>

                  <div className="diplay-inline">
                    <p>Transação (endToEndId)</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.endToEndId}
                      onChange={set("endToEndId")}
                    />
                  </div>
                  {/* É o que dá objeto ao caso: sem ele não há pagamento a contestar. */}
                  <p className="labelGray" style={{ fontSize: 12 }}>
                    Está no comprovante do PIX.
                  </p>

                  <div className="diplay-inline">
                    <p>Tipo</p>
                    <select
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.objectType}
                      onChange={set("objectType")}
                    >
                      {TIPOS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="diplay-inline">
                    <p>Motivo</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.reason}
                      onChange={set("reason")}
                    />
                  </div>

                  <div className="diplay-inline">
                    <p>Valor (opcional)</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.amount}
                      onChange={set("amount")}
                    />
                  </div>
                  {/*
                    O aviso do ponto decimal está aqui porque a alternativa é caro: o servidor
                    recusa vírgula justamente para "10,50" não ser lido como 1050.
                  */}
                  <p className="labelGray" style={{ fontSize: 12 }}>
                    Vazio = a transação inteira. Use ponto decimal (ex.: 10.50).
                  </p>

                  <div className="diplay-inline">
                    <p>Detalhes (opcional)</p>
                    <input
                      type="text"
                      className="input"
                      style={{ maxWidth: "300px" }}
                      value={form.details}
                      onChange={set("details")}
                    />
                  </div>

                  {erro ? (
                    <p style={{ color: "#b42318", fontWeight: "bold", fontSize: 13 }}>{erro}</p>
                  ) : null}

                  <div
                    className={"buttonSend " + (abrindo || !podeAbrir ? "disabled" : "")}
                    onClick={() => !abrindo && podeAbrir && abrir()}
                  >
                    {abrindo ? <div className="loader" /> : "Abrir caso"}
                  </div>
                </div>
              )}
            </section>
          </div>

          <Footer />
        </div>
      </div>
    </section>
  );
}
